import { loop } from '@web3/utils';
import { BigNumber, Contract, ethers, providers } from 'ethers';
import { formatUnits } from 'ethers/lib/utils';
import { useEffect, useMemo } from 'react';
import { useAsyncFn } from 'react-use';
import { getCache } from '../../../utils/common';
import { useThirdWallet } from '../hooks';
import { abiErc20 } from './abi';
import { getEvmRpcProvider } from './helper';

export const useEvmContract = (contractAddress: string, abi: string, chainCodeOrProvider: any) => {
  return useMemo(() => {
    let provider;
    try {
      provider =
        typeof chainCodeOrProvider === 'string' ? getEvmRpcProvider(chainCodeOrProvider) : new providers.JsonRpcProvider(chainCodeOrProvider);
    } catch (e) {
      return undefined;
    }
    return new Contract(contractAddress, abi, provider);
  }, [contractAddress, abi, chainCodeOrProvider]);
};

/**
 * 查询余额
 * @param walletAddress 钱包地址
 * @param chainCode 链chainCode
 * @param param 一些额外参数，看方法
 * @returns
 */

type BalanceOption = {
  contractAddress?: string;
  abi?: string | Array<Record<string, unknown>>;
  method?: string;
  auto?: boolean;
  interval?: number;
  decimal?: number;
};

export const useEvmBalance = (
  walletAddress?: string,
  chainCode?: string,
  { contractAddress = '', abi = '', method = 'balanceOf', decimal, auto = true, interval = 0 }: BalanceOption = {},
) => {
  const [state, action] = useAsyncFn(async () => {
    if (!chainCode || !walletAddress) {
      return undefined;
    }
    const provider = getEvmRpcProvider(chainCode);
    if (!contractAddress) {
      const res = await provider.getBalance(walletAddress);
      // native 默认按18位处理
      return formatUnits(res, decimal ? decimal : 18);
    }
    const contract = new Contract(contractAddress, abi || abiErc20, provider);
    const res: BigNumber = await contract[method](walletAddress);
    const decimalCacheKey = ['erc20-decimal', chainCode, contractAddress].join('-');
    const _decimal: number = decimal
      ? decimal
      : await getCache(decimalCacheKey, async () => {
          return contract['decimals']();
        });
    return formatUnits(res, _decimal);
  }, [walletAddress, chainCode, contractAddress, method]);
  useEffect(() => {
    if (interval > 0) {
      return loop(action, { immediate: true, interval });
    }
    if (auto) {
      action();
    }
  }, [interval, auto, action]);
  return [state, action] as const;
};

export const useWalletEvmProvider = () => {
  const walletHub = useThirdWallet();
  return useMemo(() => {
    if (!walletHub?.EVM?.provider) {
      return;
    }
    return new ethers.providers.Web3Provider(walletHub.EVM.provider);
  }, [walletHub?.EVM?.provider]);
};
